'use client'

import { KeyboardEvent, useState } from 'react'
import Image from 'next/image'
import { services } from '@/data/services'

export function ServicesSlider() {
  const [activeIndex, setActiveIndex] = useState(0)
  const activeItem = services[activeIndex]
  const isFirstSlide = activeIndex === 0
  const isLastSlide = activeIndex === services.length - 1

  function selectSlide(index: number) {
    setActiveIndex(index)
  }

  function focusSlideBullet(index: number) {
    document.getElementById(`services-slide-tab-${index}`)?.focus()
  }

  function handleBulletKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    let nextIndex: number | null = null

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = Math.max(index - 1, 0)
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = Math.min(index + 1, services.length - 1)
    }

    if (event.key === 'Home') {
      nextIndex = 0
    }

    if (event.key === 'End') {
      nextIndex = services.length - 1
    }

    if (nextIndex === null) {
      return
    }

    event.preventDefault()
    selectSlide(nextIndex)
    requestAnimationFrame(() => focusSlideBullet(nextIndex))
  }

  function goToPreviousSlide() {
    if (isFirstSlide) {
      return
    }

    selectSlide(activeIndex - 1)
  }

  function goToNextSlide() {
    if (isLastSlide) {
      return
    }

    selectSlide(activeIndex + 1)
  }

  return (
    <section
      id='servicos-especializados'
      className="w-full"
      aria-label="Serviços especializados"
    >
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {`${activeItem.title}. ${activeItem.description}`}
      </p>

      <article
        className={`flex h-90 flex-col overflow-hidden rounded-3xl px-6 py-7 shadow-[0_16px_40px_rgba(0,0,0,0.08)] transition-colors max-[340px]:h-[430px] max-[340px]:px-5 max-[340px]:py-6 md:px-10 md:py-10 ${activeItem.cardClassName}`}
      >
        <div
          className={`flex size-12 shrink-0 items-center justify-center rounded-full ${activeItem.iconClassName}`}
        >
          <Image
            src={activeItem.icon}
            alt=""
            width={24}
            height={24}
            aria-hidden="true"
            className="size-6"
          />
        </div>

        <h3 className="mt-4 font-heading text-xl font-bold leading-7 max-[340px]:text-lg max-[340px]:leading-6 md:mt-6 md:text-[32px] md:leading-10">
          {activeItem.title}
        </h3>
        <p className="mt-3 font-sans text-sm font-normal leading-5 max-[340px]:text-[13px] max-[340px]:leading-4.5 md:mt-5 md:text-base md:leading-6">
          {activeItem.description}
        </p>
      </article>

      <div className="mt-5 flex items-center justify-between gap-4">
        <div
          className="flex items-center gap-2"
          aria-label="Serviços"
        >
          {services.map((item, index) => {
            const isActive = index === activeIndex

            return (
              <button
                key={item.title}
                id={`services-slide-tab-${index}`}
                type="button"
                className="flex size-6 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2"
                aria-label={item.title}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => selectSlide(index)}
                onKeyDown={(event) => handleBulletKeyDown(event, index)}
              >
                <span
                  aria-hidden="true"
                  className={`h-2 rounded-full transition-[width,background-color] ${
                    isActive ? 'w-6 bg-white-950' : 'w-2 bg-black-100'
                  }`}
                />
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex size-11 cursor-pointer items-center justify-center rounded-full bg-lightning-yellow-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2 aria-disabled:cursor-not-allowed aria-disabled:bg-black-100"
            aria-label="Anterior"
            aria-disabled={isFirstSlide}
            aria-describedby='servicos-especializados'
            onClick={goToPreviousSlide}
          >
            <Image
              src="/assets/icons/arrow-left.svg"
              alt=""
              width={12}
              height={10}
              aria-hidden="true"
            />
          </button>

          <button
            type="button"
            className="flex size-11 cursor-pointer items-center justify-center rounded-full bg-lightning-yellow-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2 aria-disabled:cursor-not-allowed aria-disabled:bg-black-100"
            aria-label="Próximo"
            aria-disabled={isLastSlide}
            aria-describedby='servicos-especializados'
            onClick={goToNextSlide}
          >
            <Image
              src="/assets/icons/arrow-right.svg"
              alt=""
              width={12}
              height={10}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </section>
  )
}
